import React from "react";
import { Building2, Hash, Loader2, UploadCloud } from "lucide-react";
import { HotelListItem } from "@repo/types";

interface HotelHeaderProps {
  selectedHotel: HotelListItem;
  logoPreview: string | null;
  isEditMode: boolean;
  isUploadingLogo: boolean;
  handleLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const HotelHeader: React.FC<HotelHeaderProps> = ({
  selectedHotel,
  logoPreview,
  isEditMode,
  isUploadingLogo,
  handleLogoChange,
}) => {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-blue-100 dark:border-white/10 bg-linear-to-br from-blue-50 dark:from-[#111c44] via-white dark:via-[#111c44] to-blue-50 dark:to-[#111c44] p-6 shadow-sm shadow-blue-900/5 dark:shadow-none">
      <div className="flex items-start gap-5">
        <div className="relative group shrink-0">
          <div className="w-20 h-20 rounded-2xl bg-white dark:bg-[#1b254b] shadow-md flex items-center justify-center border border-gray-100 dark:border-white/5 overflow-hidden">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt={selectedHotel.HotelName}
                className={`w-full h-full object-cover transition-opacity duration-300 ${isUploadingLogo ? "opacity-30" : "opacity-100"}`}
              />
            ) : (
              <Building2
                className="text-gray-300 dark:text-slate-600"
                size={32}
              />
            )}
            {isUploadingLogo && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2
                  className="text-blue-600 dark:text-blue-400 animate-spin"
                  size={20}
                />
              </div>
            )}
          </div>

          {isEditMode && (
            <label className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
                disabled={isUploadingLogo}
              />
              <UploadCloud size={18} className="text-white" />
            </label>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-2xl font-black text-gray-900 dark:text-slate-100 truncate">
            {selectedHotel.HotelName}
          </h3>

          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div
              className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                selectedHotel.IsActive
                  ? "bg-emerald-500 text-white"
                  : "bg-red-500 text-white"
              }`}
            >
              {selectedHotel.IsActive ? "System Live" : "Maintenance"}
            </div>

            <span className="text-gray-300 dark:text-slate-600">•</span>

            <span className="text-xs font-bold text-gray-500 dark:text-slate-400 flex items-center gap-1">
              <Hash size={12} />
              ID: {selectedHotel.HotelID}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
